import { Dialog, DialogVariant, Text } from '@focaldata/cin-ui-components'
import { useContext, useEffect, useMemo, useState } from 'react'
import { ProjectType } from '../../../data/gql-gen/dashboard/graphql'
import {
  AudiencePreset,
  CUSTOM_AUDIENCE_PRESET_NAME,
  getMarketPresetLanguages,
  useGetPresetAudiencesForLanguages,
  useSetPresetAudience
} from '../../../hooks/audience/usePresetAudience'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useIsAudienceReadOnly } from '../../../hooks/useIsAudienceReadOnly'
import { useProjectData } from '../../Project/Project.hooks'
import AudienceContext, {
  setAudiencePresetWheighting
} from '../Audience.context'
import AudiencePresetPicker from './AudiencePresetPicker.control'
import AudiencePresetPickerLoading from './AudiencePresetPickerLoading'

const isCustomPreset = (newPresetName?: string) =>
  newPresetName === CUSTOM_AUDIENCE_PRESET_NAME

const AudiencePresetPickerContainer = ({
  onChoseCustomPreset
}: {
  onChoseCustomPreset?: () => void
}) => {
  const { isReadOnly } = useIsAudienceReadOnly()
  const [confirmChangeOpen, setConfirmChangeOpen] = useState<boolean>(false)
  const [presetToChangeTo, setPresetToChangeTo] = useState<AudiencePreset>()
  const [customAudienceDialogIsOpen, setCustomAudienceDialogIsOpen] =
    useState<boolean>(false)
  const { dispatch, audienceState } = useContext(AudienceContext)
  const { project } = useProjectData()
  const isFdchatProject = project?.projectType === ProjectType.FdChat

  const { setPresetAudience, loading } = useSetPresetAudience()

  const { fieldwork, loading: loadingFieldwork } = useFieldwork()
  // Union of presets across all the languages the survey fields (primary
  // locale first — its "Custom" entry is the one kept). Reading the languages
  // off the fieldwork keeps the list reactive: adding or removing a language
  // in the Market settings drawer refetches the fieldwork, which re-runs this
  // and updates the dropdown.
  const presetLanguages = useMemo(
    () => getMarketPresetLanguages(fieldwork),
    [fieldwork]
  )
  const { data: presetAudienceData, loading: loadingPresets } =
    useGetPresetAudiencesForLanguages(
      fieldwork?.locale.country || '',
      presetLanguages
    )

  const appliedPreset = fieldwork?.presetAudience

  const presets = useMemo(() => {
    let presetAudiences: AudiencePreset[] = presetAudienceData

    if (isFdchatProject) {
      presetAudiences = presetAudiences.filter(({ name }) =>
        ['Custom', 'GB Nat Rep (18+)', 'Gender', 'US Nat Rep (18+)'].includes(
          name.trim()
        )
      )
    }

    // The applied preset can be missing from the fetched lists (its locale
    // was removed from the market, or that locale's fetch failed). Once the
    // fetch has settled, keep it visible as the current selection instead of
    // mislabelling the survey as Custom. Its weightedness is unknowable
    // (fieldwork.presetAudience has no isWeighted), so fail closed: treat it
    // as weighted, which keeps criteria editing locked. If its name collides
    // with a fetched preset (same name, another locale), mark it so the two
    // entries stay distinguishable.
    if (
      !loadingPresets &&
      appliedPreset?.presetAudienceId &&
      appliedPreset.name !== CUSTOM_AUDIENCE_PRESET_NAME &&
      !presetAudiences.some(
        ({ presetAudienceId }) =>
          presetAudienceId === appliedPreset.presetAudienceId
      )
    ) {
      const nameCollides = presetAudiences.some(
        ({ name }) => name === appliedPreset.name
      )
      presetAudiences = [
        ...presetAudiences,
        {
          name: nameCollides
            ? `${appliedPreset.name} (applied)`
            : appliedPreset.name,
          presetAudienceId: appliedPreset.presetAudienceId,
          isWeighted: true
        }
      ]
    }

    return presetAudiences
  }, [presetAudienceData, loadingPresets, isFdchatProject, appliedPreset])

  const { presetAudienceId } = fieldwork?.presetAudience || {}

  useEffect(() => {
    const isPresetWeighted = presets.find(
      (p) => p.presetAudienceId === presetAudienceId && p.isWeighted
    )
    dispatch(setAudiencePresetWheighting(!!isPresetWeighted))
  }, [presets, presetAudienceId, dispatch])

  const handlePresetChosen = async (newPreset: AudiencePreset) => {
    // Re-selecting the applied preset is a no-op.
    if (newPreset.presetAudienceId === presetAudienceId) return
    // Every switch replaces the audience members server-side (dropping any
    // targeting/screening edits), so every switch is confirmed — including
    // preset → preset, where the weighted warning matters most.
    if (isCustomPreset(newPreset.name)) {
      setPresetToChangeTo(newPreset)
      setCustomAudienceDialogIsOpen(true)
    } else {
      setPresetToChangeTo(newPreset)
      setConfirmChangeOpen(true)
    }
  }

  const validatedPresetAudienceId = (
    (presetAudienceId
      ? presets.find((preset) => preset.presetAudienceId === presetAudienceId)
      : undefined) ?? presets.find((preset) => isCustomPreset(preset.name))
  )?.presetAudienceId

  // loadingPresets matters too: without it, a survey with an applied preset
  // renders early from the appended entry alone (blank Custom row, wrong
  // grouping) before the per-locale fetches settle.
  if (
    loading ||
    loadingFieldwork ||
    loadingPresets ||
    !validatedPresetAudienceId
  )
    return <AudiencePresetPickerLoading />

  const presetAudience = {
    title: 'Preset audience',
    // Weighted presets lock targeting/screening edits — say so before the
    // switch, so the locked audiences view confirms rather than surprises.
    text: `Please confirm you'd like to switch your sample to ${presetToChangeTo?.name}${
      presetToChangeTo?.isWeighted
        ? '. This is a weighted audience — its targeting and screening cannot be edited.'
        : ''
    }`,
    audienceCallback: async () => {
      if (confirmChangeOpen && presetToChangeTo) {
        await setPresetAudience(presetToChangeTo)
        setPresetToChangeTo(undefined)
      }
      setConfirmChangeOpen(false)
    }
  }

  const customAudience = {
    title: 'Custom audience',
    text: "Please confirm you'd like to change your audience to custom. If your current project includes any targeting or screening questions, these questions will be removed.",
    audienceCallback: async () => {
      if (customAudienceDialogIsOpen && presetToChangeTo) {
        await setPresetAudience(presetToChangeTo)
        onChoseCustomPreset?.()
        setPresetToChangeTo(undefined)
      }
      setCustomAudienceDialogIsOpen(false)
      onChoseCustomPreset?.()
    }
  }

  const audienceDialogContent = confirmChangeOpen
    ? presetAudience
    : customAudience

  return (
    <>
      <AudiencePresetPicker
        presets={presets}
        selectedPresetName={validatedPresetAudienceId}
        onPresetChosen={isReadOnly ? undefined : handlePresetChosen}
        isSelectedPresetWeighted={audienceState.isWeighted}
        disabled={isReadOnly}
      />
      <Dialog
        variant={DialogVariant.SingleButton}
        title={audienceDialogContent.title}
        primaryButtonText="Confirm"
        onClose={() => {
          setConfirmChangeOpen(false)
          setCustomAudienceDialogIsOpen(false)
        }}
        primaryButtonClick={audienceDialogContent.audienceCallback}
        open={confirmChangeOpen || customAudienceDialogIsOpen}
      >
        <Text>{audienceDialogContent.text}</Text>
      </Dialog>
    </>
  )
}

export default AudiencePresetPickerContainer
